import { useState, useEffect, useMemo } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useLocation, useParams, Link } from "wouter";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import type { PricingModel, Property, Room } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/hooks/use-page-title";
import { simulatePricing } from "@shared/pricing-simulation";
import { computeOccupancy } from "@shared/occupancy";

type RoomWithProperty = Room & { propertyName: string; propertyAddress: string | null; propertyStatus: string };

const WEEKS_OPTIONS = [
  "No Promotion", "6 weeks", "7 weeks", "8 weeks", "9 weeks",
  "10 weeks", "11 weeks", "12 weeks",
];

function InfoTip({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex items-center cursor-help rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Info className="h-4 w-4 text-gray-400" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs text-xs">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function formatCurrency(val: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(val);
}

function formatDateShort(iso: string) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

interface SimTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: { date: string; price: number; explanation: string } }>;
}

export function SimulationTooltip({ active, payload }: SimTooltipProps) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs max-w-56" data-testid="sim-tooltip">
      <div className="font-semibold text-gray-900 mb-1">{formatDateShort(point.date)}</div>
      <div className="text-brand font-bold text-sm mb-1">{formatCurrency(point.price)}</div>
      <div className="text-gray-500 leading-relaxed">{point.explanation}</div>
    </div>
  );
}

export default function AppPricingModelCreate() {
  const queryClient = useQueryClient();
  usePageTitle("Pricing Model");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("pricing");
  const [, navigate] = useLocation();
  const params = useParams<{ id?: string }>();
  const isEdit = !!params.id;
  const { toast } = useToast();

  const [form, setForm] = useState({
    name: "",
    preChangeBoostPercent: "10",
    minPercent: "0",
    maxPercent: "0",
    changeRatePercent: "0",
    changeRateHours: 24,
    moveInFee: "0",
    waiveMoveIn: "No",
    promoPercent: "0",
    promoWeeks: "No Promotion",
    eomPercent: "0",
    eomDays: 0,
    vacancyPercent: "0",
    vacancyDays: 0,
    occupancyPercent: "0",
  });

  const [simProperty, setSimProperty] = useState<number | null>(null);
  const [simRoom, setSimRoom] = useState<number | null>(null);
  const [enableEom, setEnableEom] = useState(true);
  const [enableVacancy, setEnableVacancy] = useState(true);
  const [enableOccupancy, setEnableOccupancy] = useState(true);

  const existingModel = useQuery<PricingModel>({
    queryKey: ["/api/app/pricing-models", params.id],
    queryFn: async () => {
      const models = await fetch("/api/app/pricing-models", { credentials: "include" }).then((r) => r.json());
      return models.find((m: PricingModel) => m.id === Number(params.id));
    },
    enabled: isEdit,
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const roomsQuery = useQuery<RoomWithProperty[]>({
    queryKey: ["/api/app/rooms"],
  });

  const pricingModelsQuery = useQuery<PricingModel[]>({
    queryKey: ["/api/app/pricing-models"],
  });

  useEffect(() => {
    if (existingModel.data) {
      const m = existingModel.data;
      setForm({
        name: m.name,
        preChangeBoostPercent: m.preChangeBoostPercent || "10",
        minPercent: m.minPercent || "0",
        maxPercent: m.maxPercent || "0",
        changeRatePercent: m.changeRatePercent || "0",
        changeRateHours: m.changeRateHours || 24,
        moveInFee: m.moveInFee || "0",
        waiveMoveIn: m.waiveMoveIn || "No",
        promoPercent: m.promoPercent || "0",
        promoWeeks: m.promoWeeks || "No Promotion",
        eomPercent: m.eomPercent || "0",
        eomDays: m.eomDays || 0,
        vacancyPercent: m.vacancyPercent || "0",
        vacancyDays: m.vacancyDays || 0,
        occupancyPercent: m.occupancyPercent || "0",
      });
    }
  }, [existingModel.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        return apiRequest("PATCH", `/api/app/pricing-models/${params.id}`, form);
      }
      return apiRequest("POST", "/api/app/pricing-models", form);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/pricing-models"] });
      toast({ title: isEdit ? "Model updated" : "Model created" });
      navigate("/app/pricing/models");
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't save this pricing model. Please try again."), variant: "destructive" });
    },
  });

  const activeProperties = (propertiesQuery.data ?? []).filter((p) => p.status === "active");
  const allRooms = roomsQuery.data ?? [];
  const simRooms = allRooms.filter(
    (r) => r.propertyId === simProperty && r.roomType !== "bathroom" && r.propertyStatus === "active" && !r.roomNumber?.toLowerCase().startsWith("bath") && !r.shadowOfRoomId
  );

  const selectedRoom = simRoom ? allRooms.find((r) => r.id === simRoom) ?? null : null;

  const propertyRooms = simProperty
    ? allRooms.filter(
        (r) => r.propertyId === simProperty && r.roomType !== "bathroom" && !r.shadowOfRoomId
      )
    : [];

  const occupancyStats = useMemo(
    () => computeOccupancy(propertyRooms, false),
    [propertyRooms]
  );

  const basePrice = selectedRoom?.currentPrice ? parseFloat(selectedRoom.currentPrice) : null;

  const simData = useMemo(() => {
    if (!basePrice) return [];
    return simulatePricing({
      basePrice,
      preChangeBoostPercent: parseFloat(form.preChangeBoostPercent) || 0,
      minPercent: parseFloat(form.minPercent) || 0,
      maxPercent: parseFloat(form.maxPercent) || 0,
      changeRatePercent: parseFloat(form.changeRatePercent) || 0,
      changeRateHours: form.changeRateHours || 24,
      promoPercent: parseFloat(form.promoPercent) || 0,
      promoWeeks: form.promoWeeks,
      eomPercent: parseFloat(form.eomPercent) || 0,
      eomDays: form.eomDays || 0,
      vacancyPercent: parseFloat(form.vacancyPercent) || 0,
      vacancyDays: form.vacancyDays || 0,
      occupancyPercent: parseFloat(form.occupancyPercent) || 0,
      occupancyPct: occupancyStats.occupancyPct,
      enableEom,
      enableVacancy,
      enableOccupancy,
    });
  }, [form, basePrice, enableEom, enableVacancy, enableOccupancy, occupancyStats.occupancyPct]);

  const assignedRuleName = selectedRoom?.repricingRule ?? null;

  const assignedModel = useMemo(() => {
    if (!assignedRuleName) return null;
    return (pricingModelsQuery.data ?? []).find((m) => m.name === assignedRuleName) ?? null;
  }, [assignedRuleName, pricingModelsQuery.data]);

  const savedModelSimData = useMemo(() => {
    if (!basePrice || !assignedModel) return [];
    return simulatePricing({
      basePrice,
      preChangeBoostPercent: parseFloat(assignedModel.preChangeBoostPercent ?? "0") || 0,
      minPercent: parseFloat(assignedModel.minPercent ?? "0") || 0,
      maxPercent: parseFloat(assignedModel.maxPercent ?? "0") || 0,
      changeRatePercent: parseFloat(assignedModel.changeRatePercent ?? "0") || 0,
      changeRateHours: assignedModel.changeRateHours || 24,
      promoPercent: parseFloat(assignedModel.promoPercent ?? "0") || 0,
      promoWeeks: assignedModel.promoWeeks ?? "No Promotion",
      eomPercent: parseFloat(assignedModel.eomPercent ?? "0") || 0,
      eomDays: assignedModel.eomDays || 0,
      vacancyPercent: parseFloat(assignedModel.vacancyPercent ?? "0") || 0,
      vacancyDays: assignedModel.vacancyDays || 0,
      occupancyPercent: parseFloat(assignedModel.occupancyPercent ?? "0") || 0,
      occupancyPct: occupancyStats.occupancyPct,
      enableEom,
      enableVacancy,
      enableOccupancy,
    });
  }, [basePrice, assignedModel, occupancyStats.occupancyPct, enableEom, enableVacancy, enableOccupancy]);

  const chartData = useMemo(
    () =>
      simData.map((p, i) => ({
        ...p,
        savedPrice: savedModelSimData[i]?.price ?? null,
        capMin: p.effectiveMinPrice,
        capMax: p.effectiveMaxPrice,
      })),
    [simData, savedModelSimData]
  );

  const yDomain = useMemo((): [number | "auto", number | "auto"] => {
    if (!simData.length) return ["auto", "auto"];
    const bounds: number[] = [
      ...simData.map((d) => d.price),
      ...savedModelSimData.map((d) => d.price),
      ...simData.flatMap((d) => (d.effectiveMinPrice !== null ? [d.effectiveMinPrice] : [])),
      ...simData.flatMap((d) => (d.effectiveMaxPrice !== null ? [d.effectiveMaxPrice] : [])),
    ];
    const lo = Math.min(...bounds);
    const hi = Math.max(...bounds);
    const pad = (hi - lo) * 0.15 || 10;
    return [Math.max(0, lo - pad), hi + pad];
  }, [simData, savedModelSimData]);

  if (isEdit && existingModel.isLoading) {
    return (
      <AppLayout>
        <div className="p-6">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-96 w-full" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-5xl" data-testid="page-pricing-model-create">
        <ViewOnlyBanner area="pricing" />
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-xl font-semibold" data-testid="text-page-title" data-tour="pricing-model-form-heading">
              {isEdit ? "Edit Pricing Model" : "Create New Pricing Model"}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Define your base pricing strategy and adjustments for this model. These settings apply only to rooms that are currently vacant.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild data-testid="button-cancel">
              <Link href="/app/pricing/models">Cancel</Link>
            </Button>
            <Button onClick={() => saveMutation.mutate()} disabled={readOnly || (!form.name || saveMutation.isPending)} data-testid="button-save-model">
              {saveMutation.isPending ? "Saving..." : "Save Model"}
            </Button>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="pricing-model-form" />
              <GuideButton pageKey="pricing-model-form" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="pricing-model-form" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <div className="space-y-6">
            <div className="border border-gray-200 rounded-lg p-5" data-tour="pricing-model-form-rates">
              <h2 className="font-semibold text-base mb-4">Model Info</h2>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Model Name</label>
                <Input
                  placeholder="Add Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="input-model-name"
                />
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-5">
              <h2 className="font-semibold text-base mb-4">Base Settings</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Pre-change boost percentage</label>
                  <InfoTip text="The percentage boost applied before any pricing change takes effect." />
                </div>
                <Input
                  type="number"
                  value={form.preChangeBoostPercent}
                  onChange={(e) => setForm({ ...form, preChangeBoostPercent: e.target.value })}
                  className="bg-gray-50"
                  data-testid="input-pre-change-boost"
                />

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Minimum Price (% from Base)</label>
                  <InfoTip text="The minimum price as a percentage deviation from the base price." />
                </div>
                <Input
                  type="number"
                  value={form.minPercent}
                  onChange={(e) => setForm({ ...form, minPercent: e.target.value })}
                  data-testid="input-min-percent"
                />

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Maximum Price (% from Base)</label>
                  <InfoTip text="The maximum price as a percentage deviation from the base price." />
                </div>
                <Input
                  type="number"
                  value={form.maxPercent}
                  onChange={(e) => setForm({ ...form, maxPercent: e.target.value })}
                  data-testid="input-max-percent"
                />

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Change Rate (price reduction)</label>
                  <InfoTip text="How fast the price drops each interval. A positive percent reduces the price by that amount per interval — compounding downward from the starting price toward the minimum cap. A negative percent raises the price instead." />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Percent</label>
                    <Input
                      type="number"
                      value={form.changeRatePercent}
                      onChange={(e) => setForm({ ...form, changeRatePercent: e.target.value })}
                      data-testid="input-change-rate-percent"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Hours</label>
                    <Input
                      type="number"
                      value={form.changeRateHours}
                      onChange={(e) => setForm({ ...form, changeRateHours: parseInt(e.target.value) || 24 })}
                      data-testid="input-change-rate-hours"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="border border-gray-200 rounded-lg p-5">
              <h2 className="font-semibold text-base mb-4">Promotions & Adjustments</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Promotional Rate</label>
                  <InfoTip text="A temporary discount applied to attract new tenants." />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Percent (0-30%)</label>
                    <Input
                      type="number"
                      min="0"
                      max="30"
                      value={form.promoPercent}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        if (e.target.value === "" || (val >= 0 && val <= 30)) {
                          setForm({ ...form, promoPercent: e.target.value });
                        }
                      }}
                      data-testid="input-promo-percent"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Weeks</label>
                    <Select value={form.promoWeeks} onValueChange={(v) => setForm({ ...form, promoWeeks: v })}>
                      <SelectTrigger className="h-9" data-testid="select-promo-weeks">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WEEKS_OPTIONS.map((w) => (
                          <SelectItem key={w} value={w}>{w}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">End of Month Adjustment</label>
                  <InfoTip text="Price adjustment applied near the end of the month to fill vacancies." />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Percent</label>
                    <Input
                      type="number"
                      value={form.eomPercent}
                      onChange={(e) => setForm({ ...form, eomPercent: e.target.value })}
                      data-testid="input-eom-percent"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Days</label>
                    <Input
                      type="number"
                      value={form.eomDays}
                      onChange={(e) => setForm({ ...form, eomDays: parseInt(e.target.value) || 0 })}
                      data-testid="input-eom-days"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Vacancy Adjustment</label>
                  <InfoTip text="Price reduction applied after a room has been vacant for a specified number of days." />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Percent</label>
                    <Input
                      type="number"
                      value={form.vacancyPercent}
                      onChange={(e) => setForm({ ...form, vacancyPercent: e.target.value })}
                      data-testid="input-vacancy-percent"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Days</label>
                    <Input
                      type="number"
                      value={form.vacancyDays}
                      onChange={(e) => setForm({ ...form, vacancyDays: parseInt(e.target.value) || 0 })}
                      data-testid="input-vacancy-days"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Occupancy Adjustment</label>
                  <InfoTip text="Price adjustment based on overall occupancy rate." />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Percent</label>
                  <Input
                    type="number"
                    value={form.occupancyPercent}
                    onChange={(e) => setForm({ ...form, occupancyPercent: e.target.value })}
                    data-testid="input-occupancy-percent"
                  />
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-5">
              <h2 className="font-semibold text-base mb-4">Additional Options</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Move In Fee</label>
                  <Input
                    type="number"
                    value={form.moveInFee}
                    onChange={(e) => setForm({ ...form, moveInFee: e.target.value })}
                    data-testid="input-move-in-fee"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Waive Move In</label>
                  <InfoTip text="Whether the move-in fee should be waived for new tenants." />
                </div>
                <Select value={form.waiveMoveIn} onValueChange={(v) => setForm({ ...form, waiveMoveIn: v })}>
                  <SelectTrigger data-testid="select-waive-move-in">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-5 mt-6">
          <h2 className="font-semibold text-base mb-1">Model Simulation</h2>
          <p className="text-sm text-gray-500 mb-4">
            Choose a property and a bedroom to preview how its price will change over the next 10 days based on the rules above.
          </p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Property</label>
              <Select
                value={simProperty ? String(simProperty) : ""}
                onValueChange={(v) => {
                  setSimProperty(v ? Number(v) : null);
                  setSimRoom(null);
                }}
              >
                <SelectTrigger data-testid="select-sim-property" data-tour="pricing-model-form-properties">
                  <SelectValue placeholder="Select a property" />
                </SelectTrigger>
                <SelectContent>
                  {activeProperties.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Bedroom</label>
              <Select
                value={simRoom ? String(simRoom) : ""}
                onValueChange={(v) => setSimRoom(v ? Number(v) : null)}
                disabled={!simProperty}
              >
                <SelectTrigger data-testid="select-sim-room">
                  <SelectValue
                    placeholder={
                      !simProperty
                        ? "Select a property first"
                        : simRooms.length === 0
                        ? "No bedrooms available for this property"
                        : "Select a bedroom"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {simRooms.map((r) => (
                    <SelectItem key={r.id} value={String(r.id)}>{r.roomNumber}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {simRoom && assignedRuleName && (
                <p className="text-xs mt-1.5" data-testid="text-room-assigned-model">
                  <span className="text-gray-500">Currently using: </span>
                  <span className="font-medium text-brand">{assignedRuleName}</span>
                  {!assignedModel && (
                    <span className="text-gray-400"> (model no longer exists)</span>
                  )}
                </p>
              )}
              {simRoom && !assignedRuleName && (
                <p className="text-xs mt-1.5 text-gray-400" data-testid="text-room-no-model">
                  No pricing model assigned to this bedroom yet
                </p>
              )}
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm" data-testid="text-sim-title">Price Simulation</h3>
              {simData.length > 0 && (
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer" data-testid="toggle-eom">
                    <Switch
                      checked={enableEom}
                      onCheckedChange={setEnableEom}
                      className="scale-75"
                    />
                    End-of-Month
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer" data-testid="toggle-vacancy">
                    <Switch
                      checked={enableVacancy}
                      onCheckedChange={setEnableVacancy}
                      className="scale-75"
                    />
                    Vacancy
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer" data-testid="toggle-occupancy">
                    <Switch
                      checked={enableOccupancy}
                      onCheckedChange={setEnableOccupancy}
                      className="scale-75"
                    />
                    Occupancy
                  </label>
                </div>
              )}
            </div>

            {!simProperty && (
              <div className="h-52 flex flex-col items-center justify-center text-gray-400" data-testid="sim-empty-property">
                <span className="text-sm">Select a property and bedroom to preview the simulation</span>
              </div>
            )}

            {simProperty && !simRoom && (
              <div className="h-52 flex flex-col items-center justify-center text-gray-400" data-testid="sim-empty-room">
                <span className="text-sm">
                  {simRooms.length === 0
                    ? "No bedrooms available for the selected property"
                    : "Select a bedroom to preview the simulation"}
                </span>
              </div>
            )}

            {simProperty && simRoom && !basePrice && (
              <div className="h-52 flex flex-col items-center justify-center text-gray-400" data-testid="sim-no-base-price">
                <span className="text-sm">This bedroom has no current price set — add a base price to run the simulation</span>
              </div>
            )}

            {simProperty && simRoom && basePrice && simData.length > 0 && (
              <div data-testid="sim-chart-container">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDateShort}
                      tick={{ fontSize: 11, fill: "#6b7280" }}
                      tickLine={false}
                      axisLine={{ stroke: "#e5e7eb" }}
                    />
                    <YAxis
                      domain={yDomain}
                      tickFormatter={(v) => `$${Math.round(v)}`}
                      tick={{ fontSize: 11, fill: "#6b7280" }}
                      tickLine={false}
                      axisLine={false}
                      width={52}
                    />
                    <RechartsTooltip content={<SimulationTooltip />} />
                    {simData.some((d) => d.effectiveMinPrice !== null) && (
                      <Line
                        type="stepAfter"
                        dataKey="capMin"
                        stroke="#ef4444"
                        strokeDasharray="4 3"
                        strokeWidth={1.5}
                        dot={false}
                        activeDot={false}
                        name="Min cap"
                        connectNulls={false}
                        legendType="none"
                      />
                    )}
                    {simData.some((d) => d.effectiveMaxPrice !== null) && (
                      <Line
                        type="stepAfter"
                        dataKey="capMax"
                        stroke="#10b981"
                        strokeDasharray="4 3"
                        strokeWidth={1.5}
                        dot={false}
                        activeDot={false}
                        name="Max cap"
                        connectNulls={false}
                        legendType="none"
                      />
                    )}
                    {assignedModel && savedModelSimData.length > 0 && (
                      <Line
                        type="monotone"
                        dataKey="savedPrice"
                        stroke="#9ca3af"
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        dot={false}
                        activeDot={{ r: 4 }}
                        name="Saved model"
                        connectNulls
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="price"
                      stroke="var(--brand)"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "var(--brand)", stroke: "#fff", strokeWidth: 1.5 }}
                      activeDot={{ r: 5 }}
                      name="Simulated Price"
                    />
                  </LineChart>
                </ResponsiveContainer>
                {simData.some((d) => d.effectiveMinPrice !== null) && (
                  <span className="sr-only" data-testid="sim-ref-line-min">Min</span>
                )}
                {simData.some((d) => d.effectiveMaxPrice !== null) && (
                  <span className="sr-only" data-testid="sim-ref-line-max">Max</span>
                )}
                <div className="mt-2 flex items-center justify-between text-xs text-gray-400 px-1">
                  <span>
                    Base: <span className="font-medium text-gray-600" data-testid="text-sim-base-price">{formatCurrency(basePrice)}</span>
                    {occupancyStats.totalConsideredRooms > 0 && (
                      <span className="ml-3">
                        Property occupancy: <span className="font-medium text-gray-600" data-testid="text-sim-occupancy">{occupancyStats.occupancyPct}%</span>
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-3 flex-wrap justify-end">
                    {simData.some((d) => d.effectiveMinPrice !== null) && (
                      <span className="flex items-center gap-1.5" data-testid="legend-cap-min">
                        <svg width="20" height="6" aria-hidden="true">
                          <line x1="0" y1="3" x2="20" y2="3" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="4 3" />
                        </svg>
                        <span>Price floor</span>
                      </span>
                    )}
                    {simData.some((d) => d.effectiveMaxPrice !== null) && (
                      <span className="flex items-center gap-1.5" data-testid="legend-cap-max">
                        <svg width="20" height="6" aria-hidden="true">
                          <line x1="0" y1="3" x2="20" y2="3" stroke="#10b981" strokeWidth="1.5" strokeDasharray="4 3" />
                        </svg>
                        <span>Price ceiling</span>
                      </span>
                    )}
                    {assignedModel && savedModelSimData.length > 0 ? (
                      <span className="flex items-center gap-1.5" data-testid="legend-saved-model">
                        <svg width="20" height="6" aria-hidden="true">
                          <line x1="0" y1="3" x2="20" y2="3" stroke="#9ca3af" strokeWidth="2" strokeDasharray="5 4" />
                        </svg>
                        <span>Saved: {assignedRuleName}</span>
                      </span>
                    ) : (
                      !simData.some((d) => d.effectiveMinPrice !== null || d.effectiveMaxPrice !== null) && (
                        <span className="text-gray-300">Hover chart to see day details</span>
                      )
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
